import { useEffect, useRef, useState } from 'react';

import { DOOGLIE_EXTENSION_ID } from '../extension';
import { noteExtensionAnswered } from '../extensionHandshake';

type UseExtensionStatusOptions = {
  /** Message sent to the extension, e.g. 'check_installed' / 'check_pinned_status'. */
  message: string;
  /** Field read off the extension response, e.g. 'installed' / 'pinned'. */
  field: string;
  /** Poll interval in ms for the first `fastPolls` polls (default 1000). */
  intervalMs?: number;
  /** Polls made at `intervalMs` before backing off (default 10). */
  fastPolls?: number;
  /** Poll interval in ms after backing off (default 5000). */
  slowIntervalMs?: number;
  /** Called once, when the status first becomes active. */
  onActive?: () => void;
};

/** What the sidebar shows: still asking, answered yes, or answered no. */
export type ExtensionStatus = 'checking' | 'installed' | 'missing';

type UseExtensionStatusResult = {
  /** True once the extension reports the requested status. */
  active: boolean;
  /** True until the first poll resolves (or it's determined chrome isn't available). */
  loading: boolean;
  /** `loading` and `active` as one value. */
  status: ExtensionStatus;
};

/**
 * Sends one message to the Dooglie extension and resolves its answer, or
 * undefined when nothing answered (not installed, switched off, or this page's
 * URL not in the extension's `externally_connectable`).
 */
export function askExtension(
  message: string,
): Promise<Record<string, unknown> | undefined> {
  return new Promise((resolve) => {
    const runtime = window.chrome?.runtime;
    if (!runtime?.sendMessage) {
      resolve(undefined);
      return;
    }
    try {
      runtime.sendMessage(DOOGLIE_EXTENSION_ID, { message }, (res) => {
        if (window.chrome.runtime.lastError) {
          resolve(undefined);
        } else {
          resolve(res as Record<string, unknown> | undefined);
        }
      });
    } catch {
      resolve(undefined);
    }
  });
}

/**
 * Whether Dooglie is pinned to the Chrome toolbar. The extension answers
 * `check_pinned_status` with `{ pinned: boolean }` from
 * `chrome.action.getUserSettings().isOnToolbar`; no answer counts as false.
 */
export async function checkPinned(): Promise<boolean> {
  const res = await askExtension('check_pinned_status');
  if (res != null) noteExtensionAnswered();
  return res?.pinned === true;
}

/**
 * Polls the Dooglie Chrome extension for a boolean status (installed / pinned).
 *
 * It polls until the first yes, then stops: an installed extension does not
 * need asking once a second for the rest of the session. While the answer is
 * no it polls every `intervalMs` for `fastPolls` polls (someone installing
 * right now sees it flip at once), then every `slowIntervalMs`, and not at all
 * while the tab is hidden. Coming back to the window or the tab asks again at
 * once and restarts the fast polls, which is when an uninstall or an install
 * in another tab would show.
 */
export function useExtensionStatus({
  message,
  field,
  intervalMs = 1000,
  fastPolls = 10,
  slowIntervalMs = 5000,
  onActive,
}: UseExtensionStatusOptions): UseExtensionStatusResult {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const activeFiredRef = useRef(false);
  const onActiveRef = useRef(onActive);
  onActiveRef.current = onActive;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    let inFlight = false;
    let pollsSinceStart = 0;

    const clearTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const schedule = () => {
      clearTimer();
      if (disposed || document.hidden) return;
      const delay = pollsSinceStart < fastPolls ? intervalMs : slowIntervalMs;
      timeoutId = setTimeout(() => void poll(), delay);
    };

    const poll = async () => {
      timeoutId = undefined;
      pollsSinceStart += 1;
      inFlight = true;
      let answeredYes = false;
      try {
        const response = await askExtension(message);
        if (disposed) return;
        setLoading(false);
        // Any answer at all means the extension is on in this browser; the
        // "switched off by a Chrome update" banner keys on having seen one.
        if (response != null) noteExtensionAnswered();

        if (response?.[field]) {
          answeredYes = true;
          setActive(true);
          if (!activeFiredRef.current) {
            activeFiredRef.current = true;
            onActiveRef.current?.();
          }
        } else {
          setActive(false);
        }
      } catch (error) {
        console.error('Error checking extension status:', error);
        if (!disposed) setLoading(false);
      } finally {
        inFlight = false;
      }
      if (!answeredYes) schedule();
    };

    // Ask now and restart the fast polls (mount, window focus, tab shown).
    const start = () => {
      if (disposed) return;
      if (!window.chrome?.runtime) {
        setActive(false);
        setLoading(false);
        return;
      }
      pollsSinceStart = 0;
      if (inFlight) return;
      clearTimer();
      void poll();
    };

    const onVisibilityChange = () => {
      if (document.hidden) clearTimer();
      else start();
    };

    start();
    window.addEventListener('focus', start);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      clearTimer();
      window.removeEventListener('focus', start);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [message, field, intervalMs, fastPolls, slowIntervalMs]);

  const status: ExtensionStatus = loading ? 'checking' : active ? 'installed' : 'missing';
  return { active, loading, status };
}
